/**
 * Conversa com a extensão através da ponte que ela injeta em guides.nolens.app
 * (apps/extension/src/content/bridge.ts). Mensagens por window.postMessage, sempre
 * para a própria origem; a extensão confirma a origem antes de aceitar.
 */
import { api, getRequestLocale } from './api';
import { config } from './config';
import { miscText } from '../i18n/misc';
import type { ExtensionLink, LinkMessage } from '@nolens/domain';

export interface ExtensionInfo {
  version: string;
  linkedEmail: string | null;
}

type FromExtension =
  | { source: 'nolens-guides-ext'; type: 'hello'; version: string; linkedEmail: string | null }
  | {
      source: 'nolens-guides-ext';
      type: 'linked';
      ok: boolean;
      email: string | null;
      error?: string;
    }
  | { source: 'nolens-guides-ext'; type: 'played'; ok: boolean; error?: string }
  | { source: 'nolens-guides-ext'; type: 'panel'; ok: boolean };

function isFromExtension(e: MessageEvent): e is MessageEvent<FromExtension> {
  return (
    e.source === window &&
    e.origin === window.location.origin &&
    (e.data as { source?: string } | null)?.source === 'nolens-guides-ext'
  );
}

/** Pergunta se a extensão está instalada (responde em menos de 1 s quando está). */
export function detectExtension(timeout = 1200): Promise<ExtensionInfo | null> {
  return new Promise((resolve) => {
    const done = (v: ExtensionInfo | null) => {
      window.removeEventListener('message', onMessage);
      clearTimeout(timer);
      resolve(v);
    };
    const onMessage = (e: MessageEvent) => {
      if (isFromExtension(e) && e.data.type === 'hello')
        done({ version: e.data.version, linkedEmail: e.data.linkedEmail });
    };
    const timer = setTimeout(() => done(null), timeout);
    window.addEventListener('message', onMessage);
    window.postMessage({ source: 'nolens-guides-web', type: 'ping' }, window.location.origin);
  });
}

/** Entrega à extensão a sessão própria dela e espera pela confirmação. */
export function sendLink(
  message: LinkMessage,
  timeout = 15000,
): Promise<{ ok: boolean; email: string | null; error?: string }> {
  return new Promise((resolve) => {
    const done = (v: { ok: boolean; email: string | null; error?: string }) => {
      window.removeEventListener('message', onMessage);
      clearTimeout(timer);
      resolve(v);
    };
    const onMessage = (e: MessageEvent) => {
      if (isFromExtension(e) && e.data.type === 'linked')
        done({
          ok: e.data.ok,
          email: e.data.email,
          ...(e.data.error ? { error: e.data.error } : {}),
        });
    };
    const timer = setTimeout(() => done({ ok: false, email: null, error: noReply() }), timeout);
    window.addEventListener('message', onMessage);
    window.postMessage(
      { source: 'nolens-guides-web', type: 'link', payload: message },
      window.location.origin,
    );
  });
}

/** Cria uma sessão para a extensão (POST /v1/extension/link) e entrega-lha. */
export async function linkExtension(): Promise<{
  ok: boolean;
  email: string | null;
  error?: string;
}> {
  const l = await api<ExtensionLink>('/v1/extension/link', { method: 'POST' });
  return sendLink({ link: l, apiUrl: config.apiUrl, webUrl: window.location.origin });
}

const SIGNED_IN_KEY = 'nolens-link-extension';

/** Antes de entrar (código ou Google): ao chegar à app com sessão, a extensão liga-se sozinha. */
export function markSignedIn(): void {
  try {
    sessionStorage.setItem(SIGNED_IN_KEY, '1');
  } catch {
    // armazenamento indisponível: fica o botão em /extensao
  }
}

/**
 * Logo a seguir a entrar pela app web: se a extensão estiver instalada e não estiver ligada a
 * esta conta, liga-a sem mais cliques (como quando se entra a partir da extensão). Só depois
 * de um login: quem saiu na extensão e continua com sessão na web não é religado.
 */
export async function linkAfterSignIn(email: string | null): Promise<void> {
  try {
    if (sessionStorage.getItem(SIGNED_IN_KEY) !== '1') return;
    sessionStorage.removeItem(SIGNED_IN_KEY);
  } catch {
    return;
  }
  // A página /extensao?ligar=1 (vinda do painel) já liga
  if (window.location.pathname === '/extensao') return;
  const ext = await detectExtension();
  if (!ext || (email && ext.linkedEmail === email)) return;
  await linkExtension().catch(() => undefined);
}

/** Pede à extensão para guiar (ou executar, auto) este guia na página (abre a primeira página num separador). */
export function playOnExtension(
  guideId: string,
  auto = false,
  timeout = 15000,
): Promise<{ ok: boolean; error?: string }> {
  return new Promise((resolve) => {
    const done = (v: { ok: boolean; error?: string }) => {
      window.removeEventListener('message', onMessage);
      clearTimeout(timer);
      resolve(v);
    };
    const onMessage = (e: MessageEvent) => {
      if (isFromExtension(e) && e.data.type === 'played')
        done({ ok: e.data.ok, ...(e.data.error ? { error: e.data.error } : {}) });
    };
    const timer = setTimeout(() => done({ ok: false, error: noReply() }), timeout);
    window.addEventListener('message', onMessage);
    window.postMessage(
      { source: 'nolens-guides-web', type: 'play', payload: { guideId, auto } },
      window.location.origin,
    );
  });
}

/**
 * «Gravar»: pede à extensão para abrir a barra lateral, na lista dos separadores. Chama-se no
 * próprio clique (o Chrome só a abre em resposta a um gesto da pessoa). false: sem resposta (uma
 * versão da extensão que não o faz) ou o Chrome recusou.
 */
export function openPanelOnExtension(timeout = 1500): Promise<boolean> {
  return new Promise((resolve) => {
    const done = (v: boolean) => {
      window.removeEventListener('message', onMessage);
      clearTimeout(timer);
      resolve(v);
    };
    const onMessage = (e: MessageEvent) => {
      if (isFromExtension(e) && e.data.type === 'panel') done(e.data.ok);
    };
    const timer = setTimeout(() => done(false), timeout);
    window.addEventListener('message', onMessage);
    window.postMessage({ source: 'nolens-guides-web', type: 'record' }, window.location.origin);
  });
}

const noReply = () => miscText(getRequestLocale()).extensionNoReply;
